"use client";

import { BaseTxButton, Step } from "logic/components";
import { type ReactNode } from "react";
import { Skeleton } from "ui/components";

import { useBatchBridgeAndRepay } from "../../useBatchedRepayTx";
import { usePendingBridgeToast } from "../../usePendingBridgeToast";

import { useLoanPayment } from "./LoanPaymentProvider";
import { QuoteUnavailableNote } from "./QuoteUnavailableNote";

interface LoanPaymentBatchTxDetailsProps {
  onDone: () => void;
}

export function LoanPaymentBatchTxDetails({ onDone }: LoanPaymentBatchTxDetailsProps) {
  const { loan, loanTimestamps, isPrepayment, isQuoteUnavailable, customPaymentAmountUnits, customPaymentAmountError } =
    useLoanPayment();
  const { isRepayment } = loanTimestamps;

  // Scoped to prepayment like the isDisabled chain below: customPaymentAmount
  // survives switching back to the Monthly Payment tab, and a stale error there
  // must not suppress the monthly simulation, which never uses that amount.
  const isAmountValid = !isPrepayment || !customPaymentAmountError;

  const {
    action,
    steps,
    currentStepIndex,

    isBalanceInsufficient,
    isBridgeInFlight,
    isLoadingBalances,
  } = useBatchBridgeAndRepay(loan, isPrepayment ? customPaymentAmountUnits : undefined, isAmountValid);

  usePendingBridgeToast();

  const currentStep = steps[currentStepIndex];
  const isDone = steps.at(-1)?.status == "confirmed";

  const isLoading = currentStep.status == "sending" || currentStep.status == "confirming";

  let isDisabled;
  if (isQuoteUnavailable) isDisabled = true;
  else if (isPrepayment) isDisabled = loanTimestamps.isGracePeriod || Boolean(customPaymentAmountError);
  else if (!isRepayment) isDisabled = true;
  else isDisabled = false;
  isDisabled = isDisabled || isBalanceInsufficient || isBridgeInFlight || isLoadingBalances;

  let buttonText: ReactNode = currentStep.action;
  if (isQuoteUnavailable) buttonText = "Payment Unavailable";
  else if (isDone) buttonText = "Done";
  else if (!isPrepayment && !isRepayment) buttonText = "Loan is Current";
  else if (isBridgeInFlight) buttonText = "Waiting for Transfer...";
  else if (isBalanceInsufficient) buttonText = "Insufficient Balance";

  return (
    <>
      <div className="flex flex-col grow px-8">
        {isQuoteUnavailable ? (
          <QuoteUnavailableNote />
        ) : isLoadingBalances ? (
          <StepsSkeleton />
        ) : (
          steps.map((step, index) => (
            <Step key={`repay-${index}`} step={step} index={index} currentStepIndex={currentStepIndex} />
          ))
        )}
      </div>

      <BaseTxButton
        onClick={isDone ? onDone : action?.send}
        disabled={isQuoteUnavailable || (isDone ? false : isDisabled || !action?.send)}
        isLoading={isDone ? false : isLoading}
        isDone={isDone}
        onDone={onDone}
      >
        {buttonText}
      </BaseTxButton>
    </>
  );
}

function StepsSkeleton() {
  return (
    <div className="flex flex-col gap-4 my-4">
      {[0, 1].map((i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="mt-1 h-4 w-4 shrink-0" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
  );
}
